import { Controller } from "@hotwired/stimulus"
import Drawflow from 'drawflow'
import { escapeHtml } from '../helpers/html'

export default class extends Controller {

  static targets = ["canvas", "input", "preview", "embed", "color"]
  static values = { data: Object }

  connect() {
    // A Turbo cache restore can bring back a previous editor's DOM
    this.canvasTarget.innerHTML = ''
    this.editor = new Drawflow(this.canvasTarget)
    this.editor.reroute = true
    this.editor.start()
    
    // Enable editor mode (allows dragging canvas)
    this.editor.editor_mode = 'edit'

    // 1. IMPORTERA
    if (this.hasDataValue && this.dataValue && Object.keys(this.dataValue).length > 0) {
      try {
        let importData = this.dataValue

        // Ta bort "graph"-lagret om det finns
        if (importData.graph) {
          importData = importData.graph
        }
        
        // Lägg till "drawflow"-lagret om det saknas
        if (!importData.drawflow) {
           importData = { "drawflow": importData }
        }

        this.rebuildNodeHtml(importData)
        this.editor.import(importData)
        
        // 2. VÄNTA PÅ ATT DOM SKA RITAS OCH UPPDATERA
        setTimeout(() => {
          if (!this.editor) return
          this.restoreNodeValues()
          // Force redraw of all connections after nodes are restored
          this.redrawAllConnections()
          // Make sure all text is visible in textareas
          this.forceTextVisible()
          
          // Additional delayed redraw to ensure connections render
          setTimeout(() => {
            if (this.editor) this.redrawAllConnections()
          }, 400)
        }, 200) // Increased timeout for better rendering
        
      } catch(e) {
        console.error("🔥 Import failed.", e)
        this.editor.clear()
        this.addStartNode()
      }
    } else {
      this.addStartNode()
    }

    // --- EVENT LISTENERS ---
    this.canvasTarget.addEventListener('click', (e) => {
      if (e.target.closest('.add-answer-btn')) {
        const nodeId = e.target.closest('.drawflow-node').id.replace('node-', '')
        this.addAnswerToNode(nodeId)
      }
      if (e.target.closest('.remove-answer-btn')) {
        const nodeId = e.target.closest('.drawflow-node').id.replace('node-', '')
        const answerRow = e.target.closest('.answer-row')
        this.removeAnswerFromNode(nodeId, answerRow)
      }
    })

    this.canvasTarget.addEventListener('input', (e) => {
      const nodeEl = e.target.closest('.drawflow-node')
      if (!nodeEl) return
      const nodeId = nodeEl.id.replace('node-', '')

      if (e.target.dataset.nodeInput) {
        const key = e.target.dataset.nodeInput
        const data = this.editor.getNodeFromId(nodeId).data

        // Extrahera UUID om det är ett ID-fält
        if (key === 'vgr_id' || key === 'private_id') {
          const extracted = this.extractUuidFromInput(e.target.value)
          data[key] = extracted
          // Uppdatera input-fältet visuellt om vi extraherade något
          if (extracted !== e.target.value) {
            e.target.value = extracted
            e.target.classList.add('ring-2', 'ring-green-400') // visuell feedback
            setTimeout(() => e.target.classList.remove('ring-2', 'ring-green-400'), 1500)
          }
        } else {
          data[key] = e.target.value
        }

        this.editor.updateNodeDataFromId(nodeId, data)
      } else if (e.target.matches('[data-answer]')) {
        this.updateNodeData(e.target)
      }

      this.syncNodeHtml(nodeId)
    })

    // Add event listeners for node movement to force connection redraw
    this.editor.on('nodeCreated', () => {
      setTimeout(() => this.forceTextVisible(), 100)
    })

    this.editor.on('nodeMoved', (id) => {
      this.editor.updateConnectionNodes(`node-${id}`)
    })

    this.editor.on('connectionCreated', () => {
      setTimeout(() => this.forceTextVisible(), 50)
    })

    // Keep the background grid aligned with the panned/zoomed canvas
    this.editor.on('translate', () => this.syncGrid())
    this.editor.on('zoom', () => this.syncGrid())
    this.syncGrid()

    // Force redraw when page becomes visible (fixes tab switching issues)
    this.handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(() => this.forceTextVisible(), 100)
      }
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    // Keep the Turbo snapshot free of editor DOM; connect() rebuilds it
    this.canvasTarget.innerHTML = ''
    this.editor = null
  }

  // ==========================================
  // 📱 PREVIEW LOGIC
  // ==========================================

  togglePreview() {
    this.previewTarget.classList.toggle('hidden')
    if (!this.previewTarget.classList.contains('hidden')) {
      this.startPreviewEngine()
    }
  }

  toggleEmbed() {
    this.embedTarget.classList.toggle('hidden')
  }

  startPreviewEngine() {
    const container = document.getElementById('preview-content')
    this.applyBrandColor()

    // Built with DOM APIs so the flow JSON never passes through an HTML string
    const widget = document.createElement('div')
    widget.className = 'p-6 flex flex-col h-full'
    widget.dataset.controller = 'widget'
    widget.dataset.widgetDataValue = JSON.stringify(this.editor.export())
    widget.innerHTML = `
      <div class="flex justify-between items-center mb-4 shrink-0">
        <button type="button"
                data-widget-target="backButton"
                data-action="click->widget#goBack"
                class="text-xs font-bold text-gray-400 hover:text-black transition-colors hidden">
          ← Bakåt
        </button>
        <div data-widget-target="backPlaceholder"></div>
      </div>
      <div class="flex-1 flex flex-col overflow-y-auto">
        <h2 data-widget-target="title" class="text-xl font-bold mb-4 text-slate-900">
          Laddar...
        </h2>
        <div data-widget-target="options" class="w-full"></div>
      </div>
    `

    // Stimulus' MutationObserver connects the widget controller once it's in the DOM
    container.replaceChildren(widget)
  }

  // Sets the CSS variables the preview's .bg-primary/.bg-accent classes read
  applyBrandColor() {
    const color = this.hasColorTarget ? this.colorTarget.value : '#4f46e5'
    const hex = color.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    const container = document.getElementById('preview-content')
    container.style.setProperty('--brand-color', color)
    container.style.setProperty('--brand-accent', `rgba(${r},${g},${b},0.08)`)
  }

  // ==========================================
  // 🛠 IMPORT/RESTORE LOGIC
  // ==========================================

  restoreNodeValues() {
    const drawflowData = this.editor.drawflow.drawflow
    if (!drawflowData) return

    const modules = Object.keys(drawflowData)
    if (modules.length === 0) return

    const activeModule = modules[0]
    const moduleData = drawflowData[activeModule]
    if (!moduleData || !moduleData.data) return
    
    const nodes = moduleData.data
    
    for (const [id, node] of Object.entries(nodes)) {
      const nodeEl = document.getElementById(`node-${id}`)
      if (!nodeEl) continue

      // 1. Generic Restorer for [data-node-input] and legacy [data-*] attributes
      // This handles: question, url, left_title, left_price, etc.
      Object.keys(node.data).forEach(key => {
        // Try new format first: [data-node-input="question"]
        let input = nodeEl.querySelector(`[data-node-input="${CSS.escape(key)}"]`)

        // Fallback to legacy format: [data-question], [data-url], etc.
        if (!input) {
          input = nodeEl.querySelector(`[data-${CSS.escape(key)}]`)
        }

        if (input && node.data[key]) {
          if (input.type === 'radio' || input.type === 'checkbox') {
             // For radios, we need to find the specific one with this value
             const radio = nodeEl.querySelector(`[data-node-input="${CSS.escape(key)}"][value="${CSS.escape(String(node.data[key]))}"]`)
             if (radio) radio.checked = true
          } else {
             // Set the value property
             input.value = node.data[key]
             
             // For textareas, ALSO update DOM text node so it's visible
             if (input.tagName === 'TEXTAREA') {
               while (input.firstChild) {
                 input.removeChild(input.firstChild)
               }
               input.appendChild(document.createTextNode(node.data[key]))
             }
          }
        }
      })

      // 2. Special Logic for Dynamic Answers
      if (node.name === 'question' && node.data.answers && Array.isArray(node.data.answers)) {
        const container = nodeEl.querySelector('.answers-container')
        if (container) {
          const currentInputs = container.querySelectorAll('[data-answer]')
          const expectedCount = node.data.answers.length
          
          if (currentInputs.length !== expectedCount) {
            container.innerHTML = node.data.answers.map((ans, index) => this.answerRowHtml(ans, index)).join('')
            this.syncNodeHtml(id)
          } else {
            const inputs = nodeEl.querySelectorAll('[data-answer]')
            inputs.forEach((input, index) => {
              if (node.data.answers[index]) input.value = node.data.answers[index]
            })
          }
        }
      }

      this.syncNodeHtml(id)
    }
  }

  syncNodeHtml(nodeId) {
    const nodeElement = document.getElementById(`node-${nodeId}`)
    if (!nodeElement) return;

    // Update textarea HTML to include current value as text content
    const textareas = nodeElement.querySelectorAll('textarea')
    textareas.forEach(txt => {
      // Clear existing content and set new value as text node
      while (txt.firstChild) {
        txt.removeChild(txt.firstChild)
      }
      if (txt.value) {
        txt.appendChild(document.createTextNode(txt.value))
      }
    })

    const inputs = nodeElement.querySelectorAll('input')
    inputs.forEach(inp => { 
        if(inp.type === 'checkbox' || inp.type === 'radio') {
            if(inp.checked) inp.setAttribute('checked', 'checked')
            else inp.removeAttribute('checked')
        } else {
            inp.setAttribute('value', inp.value) 
        }
    })

    const contentElement = nodeElement.querySelector('.drawflow_content_node')
    if (contentElement) {
      const currentHtml = contentElement.innerHTML
      const nodeObject = this.editor.getNodeFromId(nodeId)
      if (nodeObject) nodeObject.html = currentHtml
    }

    // Force connection redraw for this node
    try {
      this.editor.updateConnectionNodes(`node-${nodeId}`)
    } catch(e) {
      // Ignore errors if connections don't exist yet
    }
  }

  forceTextVisible() {
    if (!this.editor) return
    // Ensure all textareas display their values
    document.querySelectorAll('.drawflow-node textarea').forEach(el => {
      const nodeId = el.closest('.drawflow-node').id.replace('node-', '')
      const node = this.editor.getNodeFromId(nodeId)
      if (!node) return
      
      // Determine which data key this textarea is linked to
      let dataKey = null
      if (el.dataset.nodeInput) {
        dataKey = el.dataset.nodeInput
      } else if (el.dataset.question !== undefined) {
        dataKey = 'question'
      } else if (el.dataset.url !== undefined) {
        dataKey = 'url'
      }
      
      if (dataKey && node.data[dataKey]) {
        const dataValue = node.data[dataKey]
        el.value = dataValue
        // Update HTML content for proper serialization
        while (el.firstChild) {
          el.removeChild(el.firstChild)
        }
        el.appendChild(document.createTextNode(dataValue))
      }
    })
    
    document.querySelectorAll('.drawflow-node input[type="text"]').forEach(el => {
      if (el.value) {
        el.setAttribute('value', el.value)
      }
    })

    // Force all connections to redraw by updating each node
    const drawflowData = this.editor.drawflow.drawflow
    if (drawflowData) {
      const modules = Object.keys(drawflowData)
      if (modules.length > 0) {
        const nodes = drawflowData[modules[0]].data
        if (nodes) {
          Object.keys(nodes).forEach(nodeId => {
            try {
              this.editor.updateConnectionNodes(`node-${nodeId}`)
            } catch(e) {
              // Ignore
            }
          })
        }
      }
    }
  }

  redrawAllConnections() {
    // Force redraw of all connection lines
    const drawflowData = this.editor.drawflow.drawflow
    if (!drawflowData) return

    const modules = Object.keys(drawflowData)
    if (modules.length === 0) return

    const activeModule = modules[0]
    const moduleData = drawflowData[activeModule]
    if (!moduleData || !moduleData.data) return
    
    const nodes = moduleData.data
    
    // Update connections for each node
    Object.keys(nodes).forEach(nodeId => {
      try {
        this.editor.updateConnectionNodes(`node-${nodeId}`)
      } catch(e) {
        // Ignore errors for nodes without connections
      }
    })
  }

  syncGrid() {
    // .drawflow is transformed with translate(x, y) scale(zoom) around its
    // center, so the grid origin lands at center * (1 - zoom) + translate.
    const { canvas_x: x, canvas_y: y, zoom } = this.editor
    const { clientWidth: w, clientHeight: h } = this.canvasTarget
    const style = this.canvasTarget.style
    style.setProperty('--grid-size', `${50 * zoom}px`)
    style.setProperty('--grid-x', `${(w / 2) * (1 - zoom) + x}px`)
    style.setProperty('--grid-y', `${(h / 2) * (1 - zoom) + y}px`)
  }

  // --- NOD-MALLAR ---
  // Node markup always comes from these templates. Imported flows get their
  // html rebuilt here instead of trusting the html string stored in settings,
  // and restoreNodeValues() fills the inputs from node.data afterwards.

  rebuildNodeHtml(importData) {
    Object.values(importData.drawflow || {}).forEach(module => {
      Object.values(module?.data || {}).forEach(node => {
        node.html = this.nodeTemplate(node)
      })
    })
  }

  nodeTemplate(node) {
    switch (node.name) {
      case 'start':      return this.startHtml()
      case 'booking':    return this.bookingHtml()
      case 'comparison': return this.comparisonHtml()
      case 'text':       return this.textHtml()
      case 'question':
        return this.questionHtml(Array.isArray(node.data?.answers) ? node.data.answers : [])
      default:
        return `<div class="bg-white p-4 rounded-xl border border-slate-200 text-xs text-slate-500">Okänd nod: ${escapeHtml(node.name)}</div>`
    }
  }

  startHtml() {
    return `
      <div class="bg-slate-900 text-white p-4 rounded-2xl shadow-xl w-36 text-center border-2 border-slate-700 relative z-10">
        <div class="font-bold text-lg mb-1">Start 🚀</div>
        <div class="text-[10px] text-slate-400">Patienten börjar här</div>
      </div>`
  }

  textHtml() {
    return `
      <div class="bg-amber-50 rounded-2xl shadow-lg border border-amber-200 w-72 overflow-hidden relative z-10">
        <div class="bg-amber-100 px-4 py-3 border-b border-amber-200 flex items-center gap-2">
          <div class="text-lg">💬</div><span class="font-bold text-amber-900 text-sm">Fritext</span>
        </div>
        <div class="p-4 space-y-3">
          <div>
            <label class="block text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Rubrik</label>
            <input type="text" data-node-input="title" class="w-full text-sm border-amber-300 rounded-lg bg-white p-2 focus:ring-amber-500 focus:border-amber-500" placeholder="Ex. Kontakta kliniken">
          </div>
          <div>
            <label class="block text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Text</label>
            <textarea data-node-input="text" rows="4" class="w-full text-sm border-amber-300 rounded-lg bg-white p-2 resize-none focus:ring-amber-500 focus:border-amber-500" placeholder="Ex. Ring kliniken på 031-123 45 67 för vidare hjälp."></textarea>
          </div>
        </div>
      </div>`
  }

  bookingHtml() {
    return `
      <div class="bg-green-50 rounded-2xl shadow-lg border border-green-200 w-64 overflow-hidden relative z-10">
        <div class="bg-green-100 px-4 py-3 border-b border-green-200 flex items-center gap-2">
          <div class="text-lg">🏥</div><span class="font-bold text-green-900 text-sm">Webdoc Bokning</span>
        </div>
        <div class="p-4">
          <p class="text-[10px] text-green-800 mb-2 font-medium uppercase tracking-wider">Länk till bokning:</p>
          <input type="text" data-node-input="url" class="w-full text-xs border-green-300 rounded-lg bg-white p-2 focus:ring-green-500 focus:border-green-500" placeholder="https://...">
        </div>
      </div>`
  }

  comparisonHtml() {
    // Genererar ett unikt namn för radio-group så att man kan ha flera comparison-noder utan konflikt
    const radioGroup = `highlight_${Math.random().toString(36).substr(2, 9)}`

    return `
      <div class="drawflow-node-box bg-white rounded-2xl shadow-lg border border-slate-200 w-[420px] relative z-10 flex flex-col group hover:border-indigo-400 transition-colors">
        <div class="bg-indigo-50 px-4 py-3 border-b border-indigo-100 flex items-center gap-3 rounded-t-2xl">
          <div class="text-lg">⚖️</div>
          <span class="font-bold text-indigo-900 text-sm">Jämförelse / Val</span>
        </div>
        
        <div class="p-4 space-y-3">
          <div>
            <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Huvudrubrik</label>
            <input type="text" data-node-input="question" class="w-full text-xs border-slate-200 rounded-lg p-2 bg-slate-50 font-bold text-center" placeholder="Ex. Välj bokningstyp">
          </div>
          
          <div class="grid grid-cols-2 gap-3 mt-2">
            <div class="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 text-center">Alternativ 1</div>
              <div class="space-y-2">
                <input type="text" data-node-input="left_title" class="w-full text-xs border-slate-200 rounded px-2 py-1" placeholder="Rubrik (Ex. Regionen)">
                <input type="text" data-node-input="left_price" class="w-full text-xs border-slate-200 rounded px-2 py-1" placeholder="Pris (Ex. 300 kr)">
                <input type="text" data-node-input="vgr_id" class="w-full text-[10px] border-slate-200 rounded px-2 py-1 font-mono text-slate-500" placeholder="ID / VGR UUID">
                <select data-node-input="left_booking_type" class="w-full text-[10px] border-slate-200 rounded px-2 py-1 font-mono text-slate-500">
                  <option value="">Alla typer</option>
                  <option value="6">Fysisk (6)</option>
                  <option value="26">Digital (26)</option>
                </select>
                
                <label class="flex items-center gap-2 text-[10px] cursor-pointer justify-center pt-1">
                  <input type="radio" name="${radioGroup}" value="left" data-node-input="highlight" class="text-indigo-600 focus:ring-indigo-500">
                  <span class="text-slate-600 font-medium">Rekommendera</span>
                </label>
              </div>
            </div>

            <div class="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 text-center">Alternativ 2</div>
              <div class="space-y-2">
                <input type="text" data-node-input="right_title" class="w-full text-xs border-slate-200 rounded px-2 py-1" placeholder="Rubrik (Ex. Privat)">
                <input type="text" data-node-input="right_price" class="w-full text-xs border-slate-200 rounded px-2 py-1" placeholder="Pris (Ex. 1200 kr)">
                <input type="text" data-node-input="private_id" class="w-full text-[10px] border-slate-200 rounded px-2 py-1 font-mono text-slate-500" placeholder="ID / Privat UUID">
                <select data-node-input="right_booking_type" class="w-full text-[10px] border-slate-200 rounded px-2 py-1 font-mono text-slate-500">
                  <option value="">Alla typer</option>
                  <option value="6">Fysisk (6)</option>
                  <option value="26">Digital (26)</option>
                </select>
                
                <label class="flex items-center gap-2 text-[10px] cursor-pointer justify-center pt-1">
                  <input type="radio" name="${radioGroup}" value="right" data-node-input="highlight" class="text-indigo-600 focus:ring-indigo-500">
                  <span class="text-slate-600 font-medium">Rekommendera</span>
                </label>
              </div>
            </div>
          </div>

          <div class="flex justify-center pt-1">
             <label class="flex items-center gap-2 text-[10px] cursor-pointer">
                <input type="radio" name="${radioGroup}" value="none" data-node-input="highlight" class="text-slate-300 focus:ring-slate-500">
                <span class="text-slate-400 hover:text-slate-600 transition-colors">Ingen särskild rekommendation</span>
             </label>
          </div>
        </div>
      </div>`
  }

  questionHtml(answers) {
    const answersHtml = answers.map((ans, index) => this.answerRowHtml(ans, index)).join('')

    return `
      <div class="drawflow-node-box bg-white rounded-2xl shadow-lg border border-slate-200 w-80 relative z-10 flex flex-col group hover:border-indigo-400 transition-colors">
        <div class="bg-slate-50 px-4 py-3 border-b border-slate-100 flex items-center gap-3 rounded-t-2xl">
          <div class="w-6 h-6 bg-indigo-100 rounded flex items-center justify-center text-indigo-600 shadow-sm">
            <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <span class="font-bold text-slate-800 text-sm">Fråga</span>
          <i class="text-[9px] text-slate-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">ID: ${this.editor.getUuid()}</i>
        </div>
        <div class="p-4 bg-white border-b border-slate-100">
          <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Frågeställning</label>
          <textarea data-node-input="question" rows="2" class="w-full text-sm border-slate-200 bg-slate-50 rounded-lg p-2 resize-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="Skriv fråga..."></textarea>
        </div>
        <div class="p-4 bg-slate-50 rounded-b-2xl flex-1">
          <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Svarsalternativ</label>
          <div class="answers-container">
            ${answersHtml}
          </div>
          <button class="add-answer-btn mt-3 w-full py-2 border border-dashed border-indigo-300 text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-50 transition-colors flex items-center justify-center gap-1">
            <span>+</span> Lägg till alternativ
          </button>
        </div>
      </div>`
  }

  answerRowHtml(value, index = null) {
    const uniqueId = `ans_${Math.random().toString(36).substr(2, 9)}`
    const indexAttr = index === null ? '' : ` data-index="${index}"`

    return `
      <div class="answer-row flex items-center gap-2 mb-2 relative group"${indexAttr}>
        <input type="text" data-answer value="${escapeHtml(value)}" name="${uniqueId}" autocomplete="off" data-lpignore="true" placeholder="Nytt svar..." class="flex-1 text-sm border-slate-200 rounded-lg py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm transition-shadow">
        <button class="remove-answer-btn text-slate-300 hover:text-red-500 transition-colors p-1">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <div class="absolute -right-7 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none">→</div>
      </div>`
  }

  // --- NODER ---
  addStartNode() {
    try {
      const drawflowData = this.editor.drawflow.drawflow
      if (drawflowData) {
        const modules = Object.keys(drawflowData)
        if (modules.length > 0) {
          const nodes = drawflowData[modules[0]].data
          if (nodes && Object.values(nodes).some(n => n.name === 'start')) return
        }
      }
    } catch(e) {}

    this.editor.addNode('start', 0, 1, 50, 300, 'start-node', {}, this.startHtml())
  }

  addBooking() {
    this.editor.addNode('booking', 1, 0, 600, 300, 'booking-node', { url: '' }, this.bookingHtml())
  }

  addComparison() {
    const defaultData = { 
      question: 'Välj bokningstyp', 
      left_title: 'Regionen', left_price: '300 kr', vgr_id: '', 
      right_title: 'Privat', right_price: '1200 kr', private_id: '',
      highlight: 'none', // Default: Ingen highlight
      left_booking_type: '',
      right_booking_type: ''
    }

    this.editor.addNode('comparison', 1, 2, 400, 200, 'comparison-node', defaultData, this.comparisonHtml())
  }

  addText() {
    this.editor.addNode('text', 1, 0, 600, 450, 'text-node', { title: '', text: '' }, this.textHtml())
  }

  addQuestion() {
    const defaultAnswers = ["Ja", "Nej"]
    this.editor.addNode('question', 1, 2, 200, 200, 'question-node', { question: '', answers: defaultAnswers }, this.questionHtml(defaultAnswers))
  }

  // --- HJÄLPFUNKTIONER ---
  addAnswerToNode(nodeId) {
    this.editor.addNodeOutput(nodeId)
    const nodeEl = document.getElementById(`node-${nodeId}`)
    const container = nodeEl.querySelector('.answers-container')
    container.insertAdjacentHTML('beforeend', this.answerRowHtml(''))
    this.updateDataArray(nodeId)
    this.syncNodeHtml(nodeId)
  }

  removeAnswerFromNode(nodeId, rowElement) {
    const container = rowElement.parentElement
    if (container.children.length <= 1) return;
    
    const rowIndex = Array.from(container.children).indexOf(rowElement)
    rowElement.remove()
    
    const node = this.editor.getNodeFromId(nodeId)
    
    const outputKeys = Object.keys(node.outputs).sort((a, b) => {
      const numA = parseInt(a.replace('output_', ''))
      const numB = parseInt(b.replace('output_', ''))
      return numA - numB
    })
    
    if (outputKeys[rowIndex]) {
      this.editor.removeNodeOutput(nodeId, outputKeys[rowIndex])
    }
    
    const nodeEl = document.getElementById(`node-${nodeId}`)
    const outputsContainer = nodeEl.querySelector('.outputs')
    const expectedCount = container.children.length
    
    while (outputsContainer && outputsContainer.children.length > expectedCount) {
      outputsContainer.lastElementChild.remove()
    }
    
    this.updateDataArray(nodeId)
    this.syncNodeHtml(nodeId)
  }

  updateNodeData(inputElement) {
    const nodeId = inputElement.closest('.drawflow-node').id.replace('node-', '')
    this.updateDataArray(nodeId)
  }

  updateDataArray(nodeId) {
    const nodeEl = document.getElementById(`node-${nodeId}`)
    const inputs = nodeEl.querySelectorAll('[data-answer]')
    const answers = Array.from(inputs).map(input => input.value)
    const data = this.editor.getNodeFromId(nodeId).data
    data.answers = answers
    this.editor.updateNodeDataFromId(nodeId, data)
  }

  extractUuidFromInput(value) {
  // Matchar UUID-format: 8-4-4-4-12 hex-tecken
    const uuidMatch = value.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)
    return uuidMatch ? uuidMatch[0] : value.trim()
  }

  save(event) {
    event.preventDefault()
    const data = this.editor.export()
    this.inputTarget.value = JSON.stringify(data)
    this.inputTarget.form.submit()
  }
}